import type { FC } from "react";
import { memo, useEffect, useState } from "react";

import Button from "@/design-system/button";
import Carousel from "@/design-system/carousel";
import { useViewportInfoContext } from "@/src/contexts";
import type { IDiscountsPanel } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import usePageInitialScroll from "@Components/Hooks/usePageInitialScroll";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { requestTrack, urlFormat } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, type QueryXsMax, colors } from "../../styles/tokens.stylex";

export interface ICategoriesCarouselItem {
    id: string;
    name: string;
    title: string;
    navigateUrl: string;
    image: IImageLoader;
    categoryPanel?: IDiscountsPanel;
}

export interface ICategoriesCarousel {
    categories: ICategoriesCarouselItem[];
    selectedIndex?: number;
    hideSelection?: boolean;
    onClick?: (category: ICategoriesCarouselItem) => boolean | void;
    trackerAction?: string;
    clientId?: string;
}

const getCategoryItemStyle = (selected: boolean) => [styles.categoryItem, selected && styles.selected];

const renderCategory = (
    category: ICategoriesCarouselItem,
    selectedIndex: number,
    showSelected: boolean,
    index: number,
    categoryClick: (index: number) => () => boolean,
): JSX.Element => {
    const image = (
        <ImageLoader
            {...category.image}
            alt={category.name}
            srcSetAxis={"width"}
            size={300}
            styleX={styles.image}
            lazyLoading
        />
    );

    if (category.navigateUrl) {
        return (
            <Button
                key={category.name}
                as="a"
                size="None"
                variant="none"
                href={category.navigateUrl}
                styleX={getCategoryItemStyle(index === selectedIndex && showSelected)}
            >
                {image}
                <h4 {...stylex.props(styles.categoryName)}>{category.name}</h4>
            </Button>
        );
    }

    return (
        <button
            key={category.name}
            {...stylex.props(getCategoryItemStyle(index === selectedIndex && showSelected))}
            onClick={categoryClick(index)}
            type="button"
        >
            {image}
            <h4 {...stylex.props(styles.categoryName)}>{category.name}</h4>
        </button>
    );
};

const renderCarousel = (
    categories: ICategoriesCarouselItem[],
    currentIndex: number,
    isMobile: boolean,
    showSelected: boolean,
    categoryClick: (index: number) => () => boolean,
) => {
    if (!categories || categories.length === 0) {
        return null;
    }

    return (
        <Carousel showArrows={!isMobile} fullWidth styleX={styles.carousel} itemStyleX={styles.carouselItem}>
            {categories.map((category: ICategoriesCarouselItem, idx: number) =>
                renderCategory(category, currentIndex, showSelected, idx, categoryClick),
            )}
        </Carousel>
    );
};

export const CategoriesCarousel: FC<ICategoriesCarousel> = memo(
    (props) => {
        const { isMobile } = useViewportInfoContext();
        const divRef = usePageInitialScroll<HTMLDivElement>("position", props.clientId);

        const [state, setState] = useState({
            selectedIndex: props.selectedIndex || -1,
            fixCategories: false,
        });

        useEffect(() => {
            if (
                props.selectedIndex !== undefined &&
                props.selectedIndex > -1 &&
                props.selectedIndex !== state.selectedIndex
            ) {
                setState((prevState) => ({
                    selectedIndex: props.selectedIndex || -1,
                    fixCategories: prevState.fixCategories,
                }));
            }
        }, [props.selectedIndex]);

        const categoryClick = (index: number) => () => {
            const category = props.categories[index];

            let select: boolean | undefined;
            if (props.onClick) {
                select = props.onClick(category) || undefined;
            }

            if (select !== false) {
                setState({ selectedIndex: index, fixCategories: state.fixCategories });
            }

            if (props.trackerAction) {
                requestTrack(urlFormat(props.trackerAction, { id: category.id }));
            }

            return true;
        };

        const showSelected =
            !(state.fixCategories === true || props.hideSelection === false) || state.fixCategories === true;

        return (
            <div id={props.clientId} ref={divRef} {...stylex.props([styles.categoriesCarouselComponent])}>
                {renderCarousel(props.categories, state.selectedIndex, isMobile, showSelected, categoryClick)}
            </div>
        );
    },
    (prevProps, nextProps) => prevProps.categories !== nextProps.categories,
);

const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    categoriesCarouselComponent: {
        width: "100%",
    },

    carousel: {
        gap: 0,
        gridAutoColumns: {
            default: "25%",
            [xsMax]: "40%",
        },
    },
    carouselItem: {
        padding: ComposableSpacing.s1,
    },
    categoryItem: {
        padding: ComposableSpacing.s2,
        borderColor: colors.b_Primary,
        borderRadius: ComposableSpacing.s1,
        borderStyle: "solid",
        borderWidth: 1,
        alignItems: "center",
        backgroundColor: "unset",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",

        transitionDuration: "0.2s",
        transitionProperty: "border-width",
        transitionTimingFunction: "ease-out",
        width: "100%",
    },

    selected: {
        borderColor: colors.b_BrandNavy,
        borderWidth: 2,
    },

    image: {
        aspectRatio: "1/1",
        height: "100%",
        width: "100%",
    },

    categoryName: {
        margin: 0,

        overflow: "hidden",
        color: colors.grayDark,
        fontSize: {
            default: "14px",
            [smMax]: "12px",
        },
        fontWeight: 300,
        lineHeight: 1.2,
        opacity: 0.8,

        textAlign: "center",
        textOverflow: "ellipsis",
        textTransform: "uppercase",
        whiteSpace: "normal",
        height: { default: null, [xsMax]: "38px" },
        paddingTop: {
            default: "15px",
            [smMax]: "10px",
        },
    },
});
